/**
 * Owner: Masa Bayu
 * File: LandingNav.tsx
 * Date:Time: 2026-10-18 16:35
 * Description: Home landing §376 — nav crome: sidebar desktop (≥1024), rail
 *   kanan (why/authors/deals/popular), footer strip, tab bar bawah (mobile).
 *   §457 F6 — kad CTA "Become an author" dibuang daripada sidebar; penerbit
 *   daftar melalui /account → skrin Become an author.
 *   §461 — tab bar bawah tidak lagi dilukis di sini; ia memakai QxBottomNav
 *   (satu bar seapan dengan dashboard author), jadi .qxl__tabbar + .qxl__fab
 *   digugurkan.
 *   §462 — tab Home bar bawah mengembalikan paparan ke puncak skrin home, dan
 *   loceng notifikasi header home pindah menjadi tab dalam bar bawah (5 tab).
 *   §463 — tab "+ Create" digantikan "Publish" (ikon upload).
 *   Tab Publish membuka Author Dashboard. Halaman akaun ada butang yang sama.
 */

import type { ReactElement, Ref } from 'react';
import { QX_TAGLINE } from '../../../brand/brand-assets';
import { QxBottomNav } from '../../../marketplace/QxBottomNav';
import type { MarketplaceCategoryId } from '../marketplace-projection';
import type { LandingLabels } from './landing-locale';
import { AlertsPanel } from './LandingHeader';
import { LandingIcon } from './LandingIcons';
import type { LandingIconName } from './LandingIcons';
import { CATEGORY_ICON, go } from './landing-shared';
import type { LandingAuthor, LandingCategoryCount } from './landing-data';

/* ── Sidebar (desktop ≥1024) ─────────────────────────────────────────── */

function SideGroup({
  title,
  items,
  onNavigate,
}: {
  readonly title: string;
  readonly items: ReadonlyArray<{
    readonly icon: LandingIconName;
    readonly label: string;
    readonly path: string;
  }>;
  readonly onNavigate: (path: string) => void;
}): ReactElement {
  return (
    <div className="qxl__side-group">
      <p className="qxl__side-group-title">{title}</p>
      {items.map((item) => (
        <button
          key={`${item.label}-${item.path}`}
          type="button"
          className={item.path === '/' ? 'qxl__side-link is-active' : 'qxl__side-link'}
          aria-current={item.path === '/' ? 'page' : undefined}
          onClick={() => {
            onNavigate(item.path);
          }}
        >
          <LandingIcon name={item.icon} size={17} />
          {item.label}
        </button>
      ))}
    </div>
  );
}

export function LandingSidebar({
  t,
  onNavigate,
}: {
  readonly t: LandingLabels;
  readonly onNavigate: (path: string) => void;
}): ReactElement {
  return (
    <aside className="qxl__sidebar" aria-label={t.sidebar.ariaLabel}>
      <nav className="qxl__side-nav">
        <SideGroup
          title={t.sidebar.mainGroup}
          items={[
            { icon: 'home', label: t.sidebar.home, path: '/' },
            { icon: 'bookOpen', label: t.sidebar.allBooks, path: '/all-books' },
            { icon: 'sparkle', label: t.sidebar.newReleases, path: '/collections' },
            { icon: 'star', label: t.sidebar.bestsellers, path: '/bestsellers' },
            { icon: 'bolt', label: t.sidebar.deals, path: '/topics' },
            { icon: 'grid', label: t.sidebar.categories, path: '/categories' },
          ]}
          onNavigate={onNavigate}
        />
        <SideGroup
          title={t.sidebar.accountGroup}
          items={[
            { icon: 'book', label: t.sidebar.myLibrary, path: '/library' },
            { icon: 'heart', label: t.sidebar.myWishlist, path: '/library' },
            { icon: 'doc', label: t.sidebar.myOrders, path: '/orders' },
            { icon: 'pen', label: t.sidebar.createEbook, path: '/author/dashboard' },
          ]}
          onNavigate={onNavigate}
        />
        <SideGroup
          title={t.sidebar.exploreGroup}
          items={[
            { icon: 'user', label: t.sidebar.authors, path: '/authors' },
            { icon: 'leaf', label: t.sidebar.topics, path: '/topics' },
            { icon: 'filter', label: t.sidebar.genres, path: '/categories' },
            { icon: 'grid', label: t.sidebar.collections, path: '/collections' },
          ]}
          onNavigate={onNavigate}
        />
      </nav>
      <div className="qxl__side-foot">
        <img className="qxl__wordmark-img" src="/brand/qx.png" alt="QX" />
        <p>{t.sidebar.copyright}</p>
        <p className="qxl__side-foot-links">
          {t.sidebar.terms} · {t.sidebar.privacy} · {t.sidebar.help}
        </p>
      </div>
    </aside>
  );
}

/* ── Rail kanan (desktop ≥1024 · grid tablet) ────────────────────────── */

export function LandingRail({
  t,
  tc,
  authors,
  categoryCounts,
  onNavigate,
}: {
  readonly t: LandingLabels;
  readonly tc: Readonly<Record<MarketplaceCategoryId, string>>;
  readonly authors: readonly LandingAuthor[];
  readonly categoryCounts: readonly LandingCategoryCount[];
  readonly onNavigate: (path: string) => void;
}): ReactElement {
  return (
    <aside className="qxl__rail" aria-label={t.rail.authorsTitle}>
      <div className="qxl__rail-card qxl__why">
        <p className="qxl__rail-title">{t.rail.whyTitle}</p>
        <ul>
          {t.rail.whyItems.map((item) => (
            <li key={item.title}>
              <span className="qxl__why-icon" aria-hidden="true">
                <LandingIcon name={item.icon as LandingIconName} size={16} />
              </span>
              <span>
                <strong>{item.title}</strong>
                <small>{item.sub}</small>
              </span>
            </li>
          ))}
        </ul>
      </div>

      <div className="qxl__rail-card">
        <p className="qxl__rail-title">{t.rail.authorsTitle}</p>
        <ul className="qxl__authors">
          {authors.map((author) => (
            <li key={author.name}>
              <button
                type="button"
                className="qxl__author-row"
                onClick={() => {
                  go(onNavigate, `/search?author=${encodeURIComponent(author.name)}`);
                }}
              >
                <span className="qxl__author-avatar" aria-hidden="true">
                  {author.name.slice(0, 1)}
                </span>
                <span>
                  <strong>{author.name}</strong>
                  <small>{t.rail.authorBooks(author.bookCount)}</small>
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>

      <div className="qxl__rail-card qxl__deals">
        <p className="qxl__rail-title">{t.rail.dealsTitle}</p>
        <p className="qxl__deals-headline">{t.rail.dealsOff}</p>
        <p className="qxl__deals-sub">{t.rail.dealsSelected}</p>
        <button
          type="button"
          className="qxl__pill qxl__pill--primary"
          onClick={() => {
            go(onNavigate, '/topics');
          }}
        >
          {t.rail.shopNow} <LandingIcon name="arrowRight" size={15} />
        </button>
      </div>

      <div className="qxl__rail-card">
        <p className="qxl__rail-title">{t.rail.popularTitle}</p>
        <ul className="qxl__popular">
          {categoryCounts.map((entry) => (
            <li key={entry.categoryId}>
              <button
                type="button"
                className="qxl__popular-cell"
                onClick={() => {
                  go(onNavigate, `/all-books?category=${entry.categoryId}`);
                }}
              >
                <span className="qxl__why-icon" aria-hidden="true">
                  <LandingIcon name={CATEGORY_ICON[entry.categoryId]} size={16} />
                </span>
                <strong>{tc[entry.categoryId]}</strong>
                <small>{t.rail.popularCount(entry.count)}</small>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </aside>
  );
}

/* ── Footer strip (tablet/desktop) ───────────────────────────────────── */

function FooterItem({
  icon,
  title,
  sub,
}: {
  readonly icon: LandingIconName;
  readonly title: string;
  readonly sub: string;
}): ReactElement {
  return (
    <span className="qxl__footer-item">
      <span className="qxl__why-icon" aria-hidden="true">
        <LandingIcon name={icon} size={16} />
      </span>
      <span>
        <strong>{title}</strong>
        <small>{sub}</small>
      </span>
    </span>
  );
}

export function LandingFooter({ t }: { readonly t: LandingLabels }): ReactElement {
  return (
    <footer className="qxl__footer">
      <FooterItem icon="user" title={t.footer.trusted} sub={t.footer.trustedSub} />
      <FooterItem icon="lock" title={t.footer.secure} sub={t.footer.secureSub} />
      <FooterItem icon="clock" title={t.footer.support} sub={t.footer.supportSub} />
      <span className="qxl__footer-brand">
        <img className="qxl__wordmark-img" src="/brand/qx.png" alt="QX" />
        <span className="qxl__brand-text">
          <strong>{QX_TAGLINE.top}</strong>
          <small>{QX_TAGLINE.bottom}</small>
        </span>
      </span>
    </footer>
  );
}

/* ── Tab bar bawah (mobile <768) — §461: bar bersama QxBottomNav ───────── */

export function LandingTabBar({
  t,
  onNavigate,
  unread,
  alertsOpen,
  alerts,
  onBellToggle,
  alertsRef,
}: {
  readonly t: LandingLabels;
  readonly onNavigate: (path: string) => void;
  readonly unread: number;
  readonly alertsOpen: boolean;
  readonly alerts: ReadonlyArray<{ readonly id: string; readonly text: string }>;
  readonly onBellToggle: () => void;
  readonly alertsRef: Ref<HTMLDivElement>;
}): ReactElement {
  return (
    <div className="qxl__bar-alerts" ref={alertsRef}>
      <QxBottomNav
        ariaLabel={t.tabs.ariaLabel}
        items={[
          {
            id: 'home',
            icon: 'home',
            label: t.tabs.home,
            active: true,
            onSelect: () => {
              // §462 — tap Home mesti sampai skrin home; dari '/' ia sudah di
              // sana, jadi pulang ke puncak (dahului tap mati: skrol kekal).
              go(onNavigate, '/');
              try {
                window.scrollTo({ top: 0 });
              } catch {
                /* ujian jsdom: window.scrollTo belum dilaksana */
              }
            },
          },
          {
            id: 'library',
            icon: 'bookOpen',
            label: t.tabs.library,
            onSelect: () => {
              go(onNavigate, '/library');
            },
          },
          {
            id: 'publish',
            icon: 'upload',
            label: t.tabs.publish,
            onSelect: () => {
              go(onNavigate, '/author/dashboard');
            },
          },
          {
            id: 'notif',
            icon: 'bell',
            label: t.tabs.notif,
            badge: unread,
            expanded: alertsOpen,
            onSelect: onBellToggle,
          },
          {
            id: 'profile',
            icon: 'user',
            label: t.tabs.profile,
            onSelect: () => {
              go(onNavigate, '/account');
            },
          },
        ]}
      />
      {alertsOpen ? <AlertsPanel t={t} alerts={alerts} variant="bar" /> : null}
    </div>
  );
}
